import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { BulkAction } from "@itandi/itandi-bb-ui/BulkAction";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { InvoiceItemAddForm } from "~/components/common/constructions/adding/InvoiceItemAddForm";
import { ChangeAmountModal } from "~/components/common/constructions/change_amount/ChangeAmountModal";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SettlementList } from "~/components/common/settlements/list/SettlementList";
import { useDealBillings } from "~/hooks/deals/useDealBillings";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { NskResponse } from "~/models/Api";
import {
	buildBillingPageTitle,
	canDownloadBillingEstimation
} from "~/models/DealBilling";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import {
	bulkDestroy as bulkDestroyBillings,
	destroy as destroyBilling,
	create as postDealBilling,
	update as updateDealBilling,
	updateSeq
} from "~/utils/api/deals/DealBilling";
import { getDownloadPath as getBillingEstimationDownloadPath } from "~/utils/api/deals/DealBillingEstimationFile";
import { getDownloadPath } from "~/utils/api/deals/DealBillingFile";
import { create as generateBillings } from "~/utils/api/deals/DealBillingGenerate";
import { getDownloadPath as getSettlementDownloadPath } from "~/utils/api/deals/DealSettlementFile";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [showChangeAmountModal, setShowChangeAmountModal] = useState(false);
	const [bulkDeleteTarget, setBulkDeleteTarget] = useState<
		"construction" | "settlement" | null
	>(null);
	const [selectedConstructionIds, setSelectedConstructionIds] = useState<
		number[]
	>([]);
	const [selectedSettlementIds, setSelectedSettlementIds] = useState<number[]>(
		[]
	);
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const { data: deal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: currentAccount } = useItandiAccount().currentAccount();
	const { data: billingContext, mutate: mutateBillings } =
		useDealBillings().index(id, "owner");
	const { trigger: triggerBulkDelete, isMutating: isBulkDeleting } =
		useSWRMutation({ dealId: id, key: "bulk-delete-billing" }, async () => {
			if (bulkDeleteTarget === null) {
				return null;
			}
			const ids =
				bulkDeleteTarget === "construction"
					? selectedConstructionIds
					: selectedSettlementIds;
			return bulkDestroyBillings(ids, id, "owner");
		});

	if (
		deal === null ||
		billingContext === null ||
		dealConstructionItems === null ||
		currentAccount === null
	) {
		return <Loading isLoading />;
	}

	const settlementEnabled = currentAccount.company?.settlementEnabled ?? false;
	const pageTitle = buildBillingPageTitle("owner", settlementEnabled);

	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: pageTitle }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];

	const handleClickPreview = (): void => {
		const path = getDownloadPath(id, "deal_billing_owner");
		window.open(path);
	};

	const handleClickBillingEstimationPreview = (): void => {
		const path = getBillingEstimationDownloadPath(id, "owner");
		window.open(path);
	};

	const handleClickSettlementPreview = (): void => {
		const path = getSettlementDownloadPath(String(id), "owner");
		window.open(path);
	};

	const handleMutateConstructions = async (): Promise<void> => {
		await mutateBillings();
	};

	const handleCreate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit
	): Promise<NskResponse> => postDealBilling(submitValue, deal.id, "owner");

	const handleUpdate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit,
		billingId: number | null
	): Promise<NskResponse> => {
		if (billingId === null) {
			throw new Error("billingId cannot be null");
		}
		return updateDealBilling(submitValue, billingId, deal.id);
	};

	const handleDestroy = async (
		deleteBilling: DealConstruction | DealSettlement
	): Promise<NskResponse> => {
		if (deleteBilling.type !== "DealBilling") {
			throw new Error(`Illegal construction type: ${deleteBilling.type}`);
		}
		if (deleteBilling.billingType !== "owner") {
			throw new Error(`Illegal billing type: ${deleteBilling.billingType}`);
		}
		const result = await destroyBilling(deleteBilling, deal.id);
		if (isSuccess(result)) {
			setSelectedConstructionIds((prev) => {
				const index = prev.indexOf(deleteBilling.id);
				return index === -1 ? prev : prev.toSpliced(index, 1);
			});
			setSelectedSettlementIds((prev) => {
				const index = prev.indexOf(deleteBilling.id);
				return index === -1 ? prev : prev.toSpliced(index, 1);
			});
		}
		return result;
	};

	const handleUpdateSeq = async (
		newBillings: DealConstruction[] | DealSettlement[]
	): Promise<void> => {
		await updateSeq(newBillings, deal.id);
	};

	const handleToggleSelectConstruction = (id: number): void => {
		setSelectedConstructionIds((prev) => {
			const index = prev.indexOf(id);
			return index === -1 ? prev.concat(id) : prev.toSpliced(index, 1);
		});
	};

	const handleToggleSelectSettlement = (id: number): void => {
		setSelectedSettlementIds((prev) => {
			const index = prev.indexOf(id);
			return index === -1 ? prev.concat(id) : prev.toSpliced(index, 1);
		});
	};

	const handleToggleSelectAllConstructions = (ids: number[]): void => {
		setSelectedConstructionIds((prev) =>
			ids.length > 0 && ids.every((id) => prev.includes(id)) ? [] : ids
		);
	};

	const handleToggleSelectAllSettlements = (ids: number[]): void => {
		setSelectedSettlementIds((prev) =>
			ids.length > 0 && ids.every((id) => prev.includes(id)) ? [] : ids
		);
	};

	const handleBulkDelete = async (): Promise<void> => {
		if (bulkDeleteTarget === null) {
			return;
		}
		const result = await triggerBulkDelete();
		if (result !== null && isSuccess(result)) {
			await mutateBillings();
			if (bulkDeleteTarget === "construction") {
				setSelectedConstructionIds([]);
			} else {
				setSelectedSettlementIds([]);
			}
			notify({ content: "選択した項目を削除しました。", variant: "success" });
			setBulkDeleteTarget(null);
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};

	const settlementBulkDeleteButton = (
		<Buttonv2
			color="danger"
			onClick={(): void => {
				setBulkDeleteTarget("settlement");
			}}
			variant="secondary"
		>
			選択した項目を削除
		</Buttonv2>
	);

	const constructionBulkDeleteButton = (
		<Buttonv2
			color="danger"
			onClick={(): void => {
				setBulkDeleteTarget("construction");
			}}
			variant="secondary"
		>
			選択した項目を削除
		</Buttonv2>
	);

	const handleClickGenerateBillings = async (): Promise<void> => {
		const result = await generateBillings(deal.id, "owner");
		if (isSuccess(result)) {
			await mutateBillings();
			notify({ content: "請求を再作成しました。", variant: "success" });
			setIsModalOpen(false);
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};

	return (
		<ItandiAccountContext.Provider value={currentAccount}>
			<DefaultV2>
				<ClientDealLayout deal={deal} items={items}>
					<Flex justifyContent="space-between">
						<h2 className={Styles.EstimateTitle}>{pageTitle}</h2>
						{!isSp ? (
							<Flex gap={designTokens["spacing/xs"]}>
								{settlementEnabled ? (
									<Buttonv2
										disabled={billingContext.dealSettlements.length === 0}
										onClick={handleClickSettlementPreview}
										variant="secondary"
									>
										精算書ダウンロード
									</Buttonv2>
								) : null}
								<Buttonv2
									disabled={billingContext.dealConstructions.length === 0}
									onClick={handleClickPreview}
									variant="secondary"
								>
									請求書ダウンロード
								</Buttonv2>
								<Buttonv2
									disabled={
										!canDownloadBillingEstimation(
											billingContext.dealConstructions
										)
									}
									onClick={handleClickBillingEstimationPreview}
									variant="secondary"
								>
									見積書ダウンロード
								</Buttonv2>
							</Flex>
						) : null}
					</Flex>
					<ConstructionTop
						billingTarget="owner"
						constructionContext={billingContext}
						handleClickChangeAmount={(): void => {
							setShowChangeAmountModal(true);
						}}
						handleClickGenerateBillings={(): void => {
							setIsModalOpen(true);
						}}
						readonly={false}
					/>
					{showChangeAmountModal ? (
						<ChangeAmountModal
							billingContext={billingContext}
							billingType="owner"
							onClose={(): void => {
								setShowChangeAmountModal(false);
							}}
						/>
					) : null}
					{isModalOpen ? (
						<ConfirmModal
							confirmType="save"
							handleAccept={handleClickGenerateBillings}
							handleCancel={(): void => {
								setIsModalOpen(false);
							}}
							isOpen
						>
							工事見積りから請求を再作成しますか？
							<Alert marginTop={8}>
								現在登録されているオーナーへの請求情報は削除されます
							</Alert>
						</ConfirmModal>
					) : null}
					<div className={Styles.Container}>
						<div className={Styles.EstimateAddForm}>
							<InvoiceItemAddForm
								billingType="owner"
								handleCreate={handleCreate}
								mutateConstructions={handleMutateConstructions}
							/>
						</div>
						<div className={Styles.EstimateList}>
							{billingContext.dealSettlements.length > 0 && (
								<div className={Styles.SettlementSection}>
									<BulkAction
										actionsArea={settlementBulkDeleteButton}
										selectedCount={selectedSettlementIds.length}
									>
										<SettlementList
											constructionContext={billingContext}
											handleDestroy={handleDestroy}
											handleMutateSettlements={handleMutateConstructions}
											handleUpdate={handleUpdate}
											handleUpdateSeq={handleUpdateSeq}
											onToggleSelect={handleToggleSelectSettlement}
											onToggleSelectAll={handleToggleSelectAllSettlements}
											selectedIds={selectedSettlementIds}
											target="owner"
										/>
									</BulkAction>
								</div>
							)}
							<BulkAction
								actionsArea={constructionBulkDeleteButton}
								selectedCount={selectedConstructionIds.length}
							>
								<div className={Styles.ConstructionSection}>
									<ConstructionList
										companyType="client"
										constructionContext={billingContext}
										deal={deal}
										dealConstructionItems={dealConstructionItems}
										docType="billing"
										handleDestroy={handleDestroy}
										handleMutateConstructions={handleMutateConstructions}
										handleUpdate={handleUpdate}
										handleUpdateSeq={handleUpdateSeq}
										onToggleSelect={handleToggleSelectConstruction}
										onToggleSelectAll={handleToggleSelectAllConstructions}
										readonly={false}
										selectedIds={selectedConstructionIds}
										target="owner"
									/>
								</div>
							</BulkAction>
							{bulkDeleteTarget !== null ? (
								<Modalv2
									isOpen
									onClose={
										isBulkDeleting
											? (): void => undefined
											: (): void => {
													setBulkDeleteTarget(null);
												}
									}
									size="s"
								>
									<ModalHeader
										title={
											bulkDeleteTarget === "construction"
												? "施工項目の削除"
												: "精算項目の削除"
										}
									/>
									<ModalBody>
										<Paragraph>
											選択中の
											{bulkDeleteTarget === "construction"
												? "施工項目"
												: "精算項目"}
											{bulkDeleteTarget === "construction"
												? selectedConstructionIds.length
												: selectedSettlementIds.length}
											件を削除します。
										</Paragraph>
									</ModalBody>
									<ModalFooter
										leftArea={
											<Buttonv2
												disabled={isBulkDeleting}
												onClick={(): void => {
													setBulkDeleteTarget(null);
												}}
												variant="secondary"
											>
												キャンセル
											</Buttonv2>
										}
										rightArea={
											<Buttonv2
												color="danger"
												loading={isBulkDeleting}
												onClick={handleBulkDelete}
												variant="primary"
											>
												削除
											</Buttonv2>
										}
									/>
								</Modalv2>
							) : null}
						</div>
					</div>
				</ClientDealLayout>
			</DefaultV2>
		</ItandiAccountContext.Provider>
	);
}
